import { useState, useEffect, useRef } from 'react';
import { API_BASE_URL } from '@/utils/env';
import { getToken } from '@/utils/auth';
import { getAllUsers } from '@/utils/adminUsers';
import { showAlert } from '@/components/AppDialog';
import { storage } from '../firebase';
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { BRAND } from '@/brand';
import { Search, Send, Trash2, UploadCloud, Bell, CalendarPlus, LayoutGrid, SlidersHorizontal } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import ClientAccountView from '../components/ClientAccountView';
import { belongsToBrand } from '@/utils/adminScope';
import { clientId, clientIdMatches } from '@/utils/clientId';

/* Admin Command Center — per-client operational console.
   Brand-scoped: corporate admin manages accounting compliance; consulting admin
   manages capital-readiness + report delivery. Edits persist to the user record
   under `adminOps` (PUT /auth/user/:uid, admin-authorized), notifications go out
   via /send-notification, and a live preview mirrors the client dashboard.
   Re-skinned to the Level 6 admin "Command Center" mockup (Poppins, scoped .adminx). */

type AnyObj = Record<string, any>;
const isCorp = BRAND === 'corporate';

const TONES: Record<string, { dot: string; pill: string }> = {
  good: { dot: '#15803D', pill: 'ok' },
  progress: { dot: '#2563EB', pill: 'run' },
  attention: { dot: '#B45309', pill: 'warn' },
  overdue: { dot: '#DC2626', pill: 'off' },
  setup: { dot: '#475569', pill: 'wait' },
};
/* Mirrors ORDER_STATUSES in ClientAccountView, plus a "none" bucket for records
   that never had an order opened — without it those clients are unreachable by
   the status filter. */
const ORDER_FILTERS: [string, string][] = [
  ['new', 'New'],
  ['in_progress', 'In progress'],
  ['awaiting_docs', 'Awaiting docs'],
  ['delivered', 'Delivered'],
  ['cancelled', 'Cancelled'],
  ['none', 'No order'],
];
const STATUS_FILTERS: [string, string][] = [['', 'Any'], ...ORDER_FILTERS];

const TODAY = () => new Date().toISOString().slice(0, 10);
const days = (d: string) => (!d ? 99 : Math.ceil((+new Date(d) - Date.now()) / 86400000));
const fmt = (d: string) => (!d ? '-' : new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }));
const initials = (n: string) => (n || '?').split(' ').map(x => x[0]).slice(0, 2).join('').toUpperCase();

// ── Date helpers for the per-client compliance-calendar generator ───────────────
const isoDate = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const addDays = (d: Date, n: number) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
// The first quarter-end (Mar/Jun/Sep/Dec) strictly after `base`.
const nextQuarterEnd = (base: Date) => {
  for (const m of [2, 5, 8, 11]) { const d = new Date(base.getFullYear(), m + 1, 0); if (d > base) return d; }
  return new Date(base.getFullYear() + 1, 3, 0);
};
// The next occurrence of month/day (from a source date) after today.
const nextAnniversary = (monthIndex: number, day: number) => {
  const now = new Date();
  let d = new Date(now.getFullYear(), monthIndex, day);
  if (d <= now) d = new Date(now.getFullYear() + 1, monthIndex, day);
  return d;
};
const nextAnnOf = (dateStr: string) => { const s = new Date(dateStr); return nextAnniversary(s.getMonth(), s.getDate()); };

function complianceStatus(a: AnyObj) {
  if (!a.setupComplete) return { tone: 'setup', label: 'Finish setup', d: 'Onboarding incomplete' };
  if (a.overdue) return { tone: 'overdue', label: 'Action overdue', d: `${a.nextName || 'A filing'} is past due` };
  const accPend = (a.requests || []).filter((r: AnyObj) => r.status !== 'received').length || a.docRequests || 0;
  if (accPend > 0) return { tone: 'attention', label: 'Documents needed', d: `${accPend} request(s) pending` };
  const dd = days(a.nextDate);
  if (a.nextDate && dd <= 14) return { tone: 'attention', label: `Due in ${dd} days`, d: `${a.nextName} · ${fmt(a.nextDate)}` };
  return { tone: 'good', label: "You're compliant", d: a.nextDate ? `Next filing: ${fmt(a.nextDate)}` : 'All clear' };
}
function readinessStatus(c: AnyObj) {
  const pending = (c.deliverables || []).filter((x: AnyObj) => !x.downloaded);
  if (pending.length) return { tone: 'good', label: 'Your report is ready', d: pending[0].name, cta: 'Download' };
  const conPend = (c.requests || []).filter((x: AnyObj) => x.status !== 'received').length;
  if (conPend) return { tone: 'attention', label: 'Action needed', d: `${conPend} request(s) pending` };
  if (c.profile < 100 || c.readiness == null) return { tone: 'setup', label: "Let's begin", d: 'Start your assessment' };
  if (c.readiness >= 75) return { tone: 'good', label: 'Investor-ready', d: `Capital readiness ${c.readiness}/100` };
  if (c.readiness >= 50) return { tone: 'progress', label: 'Nearly investor-ready', d: `${c.readiness}/100 · close the last gaps` };
  return { tone: 'attention', label: 'Building readiness', d: `${c.readiness}/100 · key gaps remain` };
}
const statusFor = (u: AnyObj) => (isCorp ? complianceStatus(u.adminOps.acc) : readinessStatus(u.adminOps.con));

function withOps(u: AnyObj): AnyObj {
  const ops = u.adminOps || {};
  const acc = ops.acc || {}, con = ops.con || {};
  return {
    ...u,
    adminOps: {
      acc: {
        nextName: acc.nextName ?? (u.businessSetupData ? 'VAT Return Q2' : ''),
        nextDate: acc.nextDate ?? '',
        docRequests: acc.docRequests ?? 0,
        overdue: acc.overdue ?? false,
        setupComplete: acc.setupComplete ?? !!(u.accountingActivated || u.businessSetupData),
        deadlines: Array.isArray(acc.deadlines) ? acc.deadlines : [],
        deliverables: Array.isArray(acc.deliverables) ? acc.deliverables : [],
        requests: Array.isArray(acc.requests) ? acc.requests : [],
      },
      con: {
        readiness: con.readiness ?? (u.capitalReadiness?.score ?? null),
        profile: con.profile ?? (u.capitalReadiness ? 100 : u.goalAssessment ? 60 : 30),
        deliverables: Array.isArray(con.deliverables) ? con.deliverables : [],
        requests: Array.isArray(con.requests) ? con.requests : [],
      },
    },
  };
}

const TPL: Record<string, [string, string]> = {
  due: ['VAT filing due soon', "Your VAT return is due soon. We'll handle the filing, no action needed."],
  filed: ["Filed, you're compliant", 'Your VAT return has been filed. You are fully compliant.'],
  docs: ['Documents requested', 'We need a few documents to proceed. Please upload them in your Data Room.'],
  report: ['Your report is ready', 'Your Capital Readiness Report is ready to download.'],
};

const inp = 'inp';
const lbl = 'lbl';

// Data Room folders a delivered document can be filed into — must match the client
// DataRoom category ids exactly so uploads appear in the client's Data Room.
const DELIVER_CATS: { id: string; label: string }[] = isCorp
  ? [{ id: 'acc-tax', label: 'Tax Documents' }, { id: 'acc-licence', label: 'Commercial Licence' }]
  : [{ id: 'Pitch Materials', label: 'Pitch Materials' }, { id: 'Financials', label: 'Financials' }, { id: 'Legal', label: 'Legal' }, { id: 'Product', label: 'Product' }];

function Card({ title, sub, headExtra, foot, children }: { title: string; sub?: string; headExtra?: React.ReactNode; foot?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="card">
      <div className="card-h">
        <div>
          <b>{title}</b>
          {sub ? <><br /><span>{sub}</span></> : null}
        </div>
        {headExtra}
      </div>
      <div className="card-b">{children}</div>
      {foot ? <div className="card-f">{foot}</div> : null}
    </section>
  );
}

// Scoped Command Center stylesheet — mirrors the Level 6 admin mockup tokens.
// Brand accent flips via .adminx.is-corp (amber) vs .adminx (blue), matching sibling tabs.
const STYLES = `
/* Status-tone tokens; the brand accent, cards and fields come from adminShell.css. */
.adminx{--phead:#1E3A8A;
  --warn:#B45309;--warn-soft:#FEF6E7;--slate:#475569;--slate-soft:#EEF2F6;
  --red:#DC2626;--red-soft:#FEF2F2;--green:#15803D;--green-soft:#E9F7EF;}
.adminx.is-corp{--phead:#14532D;}

/* command grid */
.adminx .cc-grid{display:grid;grid-template-columns:250px minmax(0,1fr) 292px;gap:18px;align-items:start;}
.adminx .cc-clients{position:sticky;top:88px;max-height:calc(100vh - 110px);display:flex;flex-direction:column;overflow:hidden;}
.adminx .cc-search{padding:12px 14px 8px;}
.adminx .cc-search__box{position:relative;}
.adminx .cc-search__box svg{position:absolute;left:11px;top:50%;transform:translateY(-50%);width:14px;height:14px;color:var(--faint);pointer-events:none;}
.adminx .cc-search input{width:100%;padding:8px 12px 8px 32px;border:1px solid var(--line);border-radius:9px;font-size:13px;background:#fff;font-family:inherit;color:var(--ink);}
.adminx .cc-search input::placeholder{color:var(--faint);}
.adminx .cc-client .cc-id{display:block;font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;font-size:10.5px;letter-spacing:.02em;color:var(--faint);margin-top:1px;}
.adminx .cc-search input:focus{outline:none;border-color:var(--brand);box-shadow:0 0 0 3px var(--brand-soft);}
.adminx .cc-search__row{display:flex;gap:6px;align-items:stretch;}
.adminx .cc-search__row .cc-search__box{flex:1;min-width:0;}
.adminx .cc-filt-btn{position:relative;flex:none;width:34px;border:1px solid var(--line);border-radius:9px;background:#fff;cursor:pointer;display:grid;place-items:center;color:var(--muted);padding:0;}
.adminx .cc-filt-btn svg{width:14px;height:14px;}
.adminx .cc-filt-btn:hover{border-color:var(--brand);color:var(--brand);}
.adminx .cc-filt-btn.on{border-color:var(--brand);background:var(--brand-soft);color:var(--brand);}
/* Count of applied filters, so a folded panel still says it is doing something. */
.adminx .cc-filt-btn i{position:absolute;top:-5px;right:-5px;min-width:15px;height:15px;padding:0 3px;border-radius:999px;background:var(--brand);color:var(--brand-on);font-size:9.5px;font-style:normal;font-weight:700;line-height:15px;text-align:center;}
.adminx .cc-filters{margin-top:10px;padding-top:10px;border-top:1px solid var(--line-2);}
.adminx .cc-filters label{display:block;font-size:9.5px;font-weight:700;letter-spacing:.07em;text-transform:uppercase;color:var(--faint);margin:0 0 5px;}
.adminx .cc-filters label:not(:first-child){margin-top:10px;}
.adminx .cc-filters select{width:100%;padding:6px 8px;border:1px solid var(--line);border-radius:8px;font-size:12px;font-family:inherit;color:var(--ink);background:#fff;cursor:pointer;}
.adminx .cc-filters select:focus{outline:none;border-color:var(--brand);box-shadow:0 0 0 3px var(--brand-soft);}
.adminx .cc-filters select:disabled{background:var(--slate-soft);color:var(--faint);cursor:not-allowed;}
/* Chips wrap: six order statuses cannot sit on one line in a 250px rail. */
.adminx .cc-chips{display:flex;flex-wrap:wrap;gap:4px;}
.adminx .cc-chips button{border:1px solid var(--line);border-radius:999px;background:#fff;color:var(--muted);font-family:inherit;font-size:11px;font-weight:600;padding:3px 9px;cursor:pointer;}
.adminx .cc-chips button:hover{border-color:var(--brand);color:var(--brand);}
.adminx .cc-chips button.on{background:var(--brand);border-color:var(--brand);color:var(--brand-on);}
.adminx .cc-filt-clear{margin-top:10px;width:100%;border:1px solid var(--line);border-radius:8px;background:#fff;color:var(--muted);font-family:inherit;font-size:11.5px;font-weight:600;padding:5px 8px;cursor:pointer;}
.adminx .cc-filt-clear:hover{border-color:var(--red);color:var(--red);}
.adminx .cc-list{overflow-y:auto;}
.adminx .cc-client{display:flex;align-items:center;gap:10px;width:100%;text-align:left;padding:11px 14px;border:none;border-bottom:1px solid var(--line-2);background:transparent;cursor:pointer;font-family:inherit;}
.adminx .cc-client:hover{background:#FCFDFE;}
.adminx .cc-client.active{background:var(--brand-soft);}
.adminx .cc-client b{display:block;font-size:12.5px;line-height:1.3;color:var(--ink);}
.adminx .cc-client span:not(.avatar){font-size:11px;color:var(--muted);display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:150px;}
.adminx .cc-stack{display:flex;flex-direction:column;gap:18px;min-width:0;}
.adminx .cc-placeholder{color:var(--faint);text-align:center;padding:60px 0;font-size:13px;}

/* avatars */
.adminx .avatar{border-radius:50%;background:var(--brand);color:var(--brand-on);display:grid;place-items:center;font-weight:600;flex:none;}
.adminx .cc-client .avatar{width:32px;height:32px;font-size:11.5px;}
.adminx .avatar--lg{width:44px;height:44px;font-size:15px;}

/* client header */
.adminx .cc-head{display:flex;align-items:center;gap:14px;}
.adminx .cc-head__meta{flex:1;min-width:0;}
.adminx .cc-head__meta b{font-size:15px;display:block;color:var(--ink);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}

/* pills */
.adminx .pill{display:inline-flex;align-items:center;gap:6px;font-size:11.5px;font-weight:600;padding:4px 10px;border-radius:999px;white-space:nowrap;}
.adminx .pill::before{content:"";width:6px;height:6px;border-radius:50%;background:currentColor;}
.adminx .pill.ok{background:var(--green-soft);color:var(--green);}
.adminx .pill.wait{background:var(--slate-soft);color:var(--slate);}
.adminx .pill.off{background:var(--red-soft);color:var(--red);}
.adminx .pill.warn{background:var(--warn-soft);color:var(--warn);}
.adminx .pill.run{background:var(--brand-soft);color:var(--brand-d);}
.adminx .pill.plain{background:var(--line-2);color:var(--muted);}/* fields */
.adminx textarea.inp{resize:vertical;min-height:84px;}
.adminx .inp::placeholder{color:var(--faint);}.adminx .frow{display:grid;gap:14px;}
.adminx .frow--2{grid-template-columns:1fr 1fr;}
.adminx .frow--3{grid-template-columns:1fr 1fr 1fr;}
@media(max-width:640px){.adminx .frow--2,.adminx .frow--3{grid-template-columns:1fr;}}
.adminx .row-gap{margin-top:14px;}.adminx .saved{font-size:12px;font-weight:600;color:var(--green);transition:opacity .2s;margin-left:auto;white-space:nowrap;}/* buttons */
.adminx .btn-sm{padding:6px 11px;font-size:12.5px;border-radius:8px;}.adminx .push-right{margin-left:auto;}

/* channel chips */
.adminx .chan{display:flex;gap:8px;flex-wrap:wrap;}
.adminx .chan button{border:1px solid var(--line);border-radius:999px;padding:6px 13px;font-size:12px;font-weight:600;color:var(--muted);display:inline-flex;align-items:center;gap:6px;background:#fff;cursor:pointer;font-family:inherit;}
.adminx .chan button::before{content:"";width:7px;height:7px;border-radius:50%;background:var(--faint);}
.adminx .chan button.active{border-color:var(--green);color:var(--green);background:var(--green-soft);}
.adminx .chan button.active::before{background:var(--green);}

/* file drop */
.adminx .file-drop{display:flex;align-items:center;gap:10px;border:1.5px dashed var(--line);border-radius:10px;padding:14px 16px;font-size:12.5px;color:var(--muted);cursor:pointer;background:#FBFCFD;}
.adminx .file-drop:hover{border-color:var(--brand);color:var(--ink-2);}
.adminx .file-drop.is-uploading{border-color:var(--brand);background:var(--brand-soft);color:var(--brand-d);}
.adminx .file-drop.has{border-style:solid;border-color:var(--green);background:var(--green-soft);color:var(--green);font-weight:600;}
.adminx .file-drop svg{flex:none;}
.adminx .file-drop span{flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.adminx .file-drop input{display:none;}
.adminx .drop-link{font-size:11.5px;font-weight:600;color:var(--brand-d);margin-top:8px;display:inline-block;text-decoration:none;}

/* deliverables list */
.adminx .deliv-list{margin-top:6px;display:flex;flex-direction:column;}
.adminx .deliv-row{display:flex;align-items:center;gap:12px;padding:10px 0;border-bottom:1px solid var(--line-2);}
.adminx .deliv-row:last-child{border-bottom:0;}
.adminx .deliv-badge{width:34px;height:34px;border-radius:9px;background:var(--green-soft);color:var(--green);display:grid;place-items:center;font-size:9.5px;font-weight:700;flex:none;}
.adminx .deliv-meta{flex:1;min-width:0;}
.adminx .deliv-meta b{display:block;font-size:13px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.adminx .deliv-meta span{font-size:11.5px;color:var(--muted);}
.adminx .deliv-open{font-size:11.5px;font-weight:600;color:var(--brand-d);text-decoration:none;flex:none;}
.adminx .deliv-pill{font-size:10px;font-weight:700;padding:3px 8px;border-radius:999px;flex:none;}
.adminx .deliv-pill.delivered{background:var(--brand-soft);color:var(--brand-d);}
.adminx .deliv-pill.done{background:var(--green-soft);color:var(--green);}
.adminx .icon-btn{background:none;border:none;color:var(--faint);padding:4px;cursor:pointer;line-height:0;flex:none;}
.adminx .icon-btn:hover{color:var(--red);}

/* compliance calendar (editable rows) */
.adminx .cal-tools{display:flex;gap:10px;flex-wrap:wrap;margin-bottom:10px;}
.adminx .cal-list{display:flex;flex-direction:column;}
.adminx .cal-row{display:flex;align-items:center;gap:10px;padding:9px 0;border-bottom:1px solid var(--line-2);}
.adminx .cal-row:last-child{border-bottom:0;}
.adminx .cal-row .cal-name{flex:1;min-width:0;}
.adminx .cal-row .cal-date{width:150px;flex:none;}
.adminx .cal-row .cal-type{width:120px;flex:none;}
@media(max-width:640px){.adminx .cal-row{flex-wrap:wrap;}.adminx .cal-row .cal-date,.adminx .cal-row .cal-type{width:auto;flex:1;}}

/* preview */
.adminx .preview{position:sticky;top:88px;}
.adminx .phead{background:var(--phead);color:#fff;border-radius:12px 12px 0 0;padding:12px 16px;display:flex;justify-content:space-between;align-items:center;font-size:12px;font-weight:600;}
.adminx .phead span{font-size:10px;letter-spacing:.8px;opacity:.75;text-transform:uppercase;}
.adminx .pbody{border:1px solid var(--line);border-top:0;border-radius:0 0 12px 12px;background:#fff;padding:14px;}
.adminx .pcard{border:1px solid var(--line);border-radius:10px;padding:12px;margin-bottom:12px;}
.adminx .pcard b{font-size:12.5px;display:flex;align-items:center;gap:7px;}
.adminx .pcard p{font-size:11.5px;color:var(--muted);margin-top:2px;}
.adminx .pcard a{display:inline-block;margin-top:6px;font-size:11.5px;font-weight:600;color:var(--brand-d);text-decoration:none;}
.adminx .plabel{font-size:10px;font-weight:600;letter-spacing:.8px;text-transform:uppercase;color:var(--faint);margin:12px 0 8px;display:flex;align-items:center;gap:6px;}
.adminx .pnote{border-bottom:1px solid var(--line-2);padding:8px 2px;}
.adminx .pnote:last-child{border-bottom:0;}
.adminx .pnote b{font-size:11.5px;display:block;}
.adminx .pnote p{font-size:11px;color:var(--muted);margin-top:1px;}
.adminx .pnote time{font-size:10px;color:var(--faint);}
.adminx .pempty{font-size:11.5px;color:var(--faint);text-align:center;padding:10px 0;}
.adminx .dot{width:8px;height:8px;border-radius:50%;display:inline-block;flex:none;}

/* detail view tabs (Operations | Account) */
.adminx .cc-tabs{display:inline-flex;gap:4px;background:#EEF1F5;border:1px solid var(--line);border-radius:11px;padding:4px;}
.adminx .cc-tabs button{border:none;background:transparent;font-family:inherit;font-size:12.5px;font-weight:600;color:var(--muted);padding:7px 16px;border-radius:8px;cursor:pointer;display:inline-flex;align-items:center;gap:7px;}
.adminx .cc-tabs button.active{background:#fff;color:var(--ink);box-shadow:var(--sh);}
.adminx .cc-tabs button:not(.active):hover{color:var(--ink-2);}/* empty state */
.adminx .empty b{font-size:14px;display:block;color:var(--ink);}
.adminx .empty p{font-size:12.5px;color:var(--muted);margin-top:4px;}

/* responsive */
@media(max-width:1180px){.adminx .cc-grid{grid-template-columns:230px minmax(0,1fr);}.adminx .preview{display:none;}}
@media(max-width:900px){.adminx .cc-grid{grid-template-columns:1fr;}.adminx .cc-clients{position:static;max-height:320px;}}
`;

export default function AdminCommandCenter() {
  const [list, setList] = useState<AnyObj[]>([]);
  const [loadingList, setLoadingList] = useState(true); // so the first (slow) load shows "Loading…", not "No clients"
  const [q, setQ] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [fStatus, setFStatus] = useState('');   // orderStatus key, '' = any
  const [fState, setFState] = useState('');     // account lifecycle, '' = any
  const [fExtra, setFExtra] = useState('');     // plan (corporate) / assessment (consulting)
  const [sort, setSort] = useState<'new' | 'name' | 'seen'>('new');
  const [cur, setCur] = useState<AnyObj | null>(null);
  const [flash, setFlash] = useState(false);
  const [notif, setNotif] = useState({ tpl: '', title: '', msg: '', inApp: true, wa: true });
  const [deliver, setDeliver] = useState({ name: '', date: '', url: '', cat: DELIVER_CATS[0]?.id ?? '' });
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState<number | null>(null);
  const [sent, setSent] = useState<AnyObj[]>([]);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => { fetchList(); /* eslint-disable-next-line */ }, []);

  async function fetchList() {
    try {
      const arr: AnyObj[] = await getAllUsers();
      const scoped = arr.filter(u => belongsToBrand(u, isCorp));
      /* The list is filtered and sorted client-side, so the row has to carry
         every field a filter reads. /auth/users/all returns a deliberately
         whitelisted projection (heavy nested blobs blow Cloud Run's response
         limit), so these are all of the filterable fields there are — notably
         NOT adminOps or referredBy, which only the per-client fetch returns. */
      const mapped = scoped.map(u => ({
        uid: u._id,
        name: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || 'Client',
        email: u.email || '',
        company: u.businessSetupData?.companyName || '',
        brand: u.brand,
        orderStatus: u.orderStatus || '',
        emailVerified: !!u.emailVerified,
        onboardingCompleted: !!u.onboardingCompleted,
        activated: isCorp ? !!u.accountingActivated : !!u.consultingActivated,
        plan: u.businessSetupData?.planName || u.purchasedPackages?.[0]?.name || '',
        country: u.country || '',
        assessed: !!u.capitalAssessment?.completedAt,
        createdAt: u.createdAt || '',
        lastLoginAt: u.lastLoginAt || '',
      }));
      setList(mapped);
      const wanted = new URLSearchParams(window.location.search).get('client');
      const initial = (wanted && mapped.some(m => m.uid === wanted)) ? wanted : mapped[0]?.uid;
      if (initial) selectClient(initial);
    } catch (e) { console.error('load clients failed', e); }
    finally { setLoadingList(false); }
  }

  async function selectClient(uid: string) {
    setSent([]);
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE_URL}/auth/user/${uid}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setCur(withOps({ uid, ...(data.user || {}) }));
    } catch (e) { console.error('load client failed', e); }
  }

  function patchOps(fn: (ops: AnyObj) => void) {
    setCur(prev => {
      if (!prev) return prev;
      const next = JSON.parse(JSON.stringify(prev));
      fn(next.adminOps);
      scheduleSave(next);
      return next;
    });
  }
  function scheduleSave(next: AnyObj) {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => saveOps(next), 600);
  }
  async function saveOps(next: AnyObj) {
    try {
      const token = getToken();
      await fetch(`${API_BASE_URL}/auth/user/${next.uid}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ adminOps: next.adminOps }),
      });
      setFlash(true); setTimeout(() => setFlash(false), 1400);
    } catch (e) { console.error('save ops failed', e); }
  }

  async function sendNotification(title: string, msg: string, ch: string[]) {
    if (!cur || !title) return;
    const notification = { title, subtitle: msg, message: msg, type: 'message', priority: 'Normal', channels: ch, sentAt: new Date().toISOString(), read: false };
    setSent(s => [{ title, msg, ch, time: 'now' }, ...s]);
    try {
      const token = getToken();
      await fetch(`${API_BASE_URL}/auth/send-notification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ uids: [cur.uid], notification }),
      });
    } catch (e) { console.error('send notification failed', e); }
  }

  function pushComposed() {
    if (!notif.title) return;
    const ch: string[] = []; if (notif.inApp) ch.push('In-app'); if (notif.wa) ch.push('WhatsApp');
    sendNotification(notif.title, notif.msg, ch);
    setNotif({ tpl: '', title: '', msg: '', inApp: true, wa: true });
  }
  // Upload a real file into the client's Data Room (Firebase Storage dataRoom/{uid}/{cat}),
  // mirroring the client's own uploader so it appears in their Data Room page.
  async function uploadDeliverable(file: File) {
    if (!cur || !file) return;
    setUploading(true); setUploadPct(0);
    try {
      const ts = Date.now();
      const safe = (file.name.trim().replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_.-]/g, '_')) || `file_${ts}`;
      let adminEmail = 'admin';
      try { adminEmail = JSON.parse(localStorage.getItem('adminUser') || '{}').email || 'admin'; } catch { /* noop */ }
      const path = `dataRoom/${cur.uid}/${deliver.cat}/${ts}_${safe}`;
      const task = uploadBytesResumable(storageRef(storage, path), file, {
        customMetadata: { originalName: file.name, uploadedBy: adminEmail, uploadedByAdmin: 'true' },
      });
      const url: string = await new Promise((resolve, reject) => {
        task.on('state_changed',
          s => setUploadPct(Math.round((s.bytesTransferred / s.totalBytes) * 100)),
          reject,
          async () => resolve(await getDownloadURL(task.snapshot.ref)),
        );
      });
      setDeliver(d => ({ ...d, url, name: d.name || file.name }));
    } catch (e) {
      console.error('deliverable upload failed', e);
      showAlert('Upload failed. Please try again.');
    } finally {
      setUploading(false); setUploadPct(null);
    }
  }

  function publishDoc() {
    if (!deliver.url) { showAlert('Upload a file first.'); return; }
    const name = deliver.name || 'Document', date = deliver.date || TODAY(), url = deliver.url, cat = deliver.cat;
    patchOps(ops => { ops[isCorp ? 'acc' : 'con'].deliverables.unshift({ name, date, url, cat, downloaded: false }); });
    sendNotification('A document is ready for you', `${name} has been added to your Data Room.`, ['In-app']);
    setDeliver({ name: '', date: '', url: '', cat: DELIVER_CATS[0]?.id ?? '' });
  }

  // Generate a per-client compliance calendar from the client's real dates
  // (trade-licence expiry / incorporation) + standard UAE obligations. Non-destructive:
  // adds any obligation not already present (by name); the admin reviews & publishes.
  function generateCalendar() {
    if (!cur) return;
    const incRaw = cur.company?.incorporationDate || cur.company?.tradeLicense?.issueDate || cur.businessSetupData?.submittedAt || cur.createdAt;
    const licRaw = cur.company?.tradeLicense?.expiryDate || cur.company?.tradeLicense?.expiry || cur.company?.licenseExpiry;
    const now = new Date();
    const items: AnyObj[] = [];
    // Trade Licence Renewal — licence expiry if known, else the next incorporation anniversary.
    if (licRaw) items.push({ name: 'Trade Licence Renewal', date: isoDate(new Date(licRaw)), type: 'Licence' });
    else if (incRaw) items.push({ name: 'Trade Licence Renewal', date: isoDate(nextAnnOf(incRaw)), type: 'Licence' });
    // VAT Return — next quarter end + 28 days.
    items.push({ name: 'VAT Return', date: isoDate(addDays(nextQuarterEnd(now), 28)), type: 'VAT' });
    // Corporate Tax Return — 9 months after a Dec-31 financial year end (≈ 30 Sep).
    items.push({ name: 'Corporate Tax Return', date: isoDate(nextAnniversary(8, 30)), type: 'Corporate Tax' });
    // Annual confirmation / ESR — incorporation anniversary if known.
    if (incRaw) items.push({ name: 'Annual Confirmation / ESR', date: isoDate(nextAnnOf(incRaw)), type: 'ESR' });

    patchOps(o => {
      const existing = new Set((o.acc.deadlines || []).map((d: AnyObj) => d.name));
      items.forEach(it => { if (!existing.has(it.name)) o.acc.deadlines.push(it); });
      o.acc.deadlines.sort((a: AnyObj, b: AnyObj) => (a.date || '').localeCompare(b.date || ''));
    });
  }

  // Itemized documents/actions the client must provide (onboarding docs + active-client
  // ID renewals / updated docs / additional info). Shows at the top of their dashboard.
  function renderRequests() {
    if (!cur) return null;
    const bk = isCorp ? 'acc' : 'con';
    const reqs: AnyObj[] = cur.adminOps[bk].requests || [];
    const KINDS = [
      { v: 'document', l: 'Document' },
      { v: 'id-renewal', l: 'ID renewal' },
      { v: 'update', l: 'Updated document' },
      { v: 'info', l: 'Additional info' },
    ];
    // Keep the legacy numeric docRequests in sync so existing status logic still fires.
    const syncCount = (o: AnyObj) => { o[bk].docRequests = (o[bk].requests || []).filter((r: AnyObj) => r.status !== 'received').length; };
    const pendingN = reqs.filter(r => r.status !== 'received').length;
    return (
      <Card
        title="Requests to client"
        sub="Documents & actions the client must provide, shown at the top of their dashboard & Notification Center"
        headExtra={<span>{pendingN} pending</span>}
      >
        <div className="cal-tools">
          <button className="btn" onClick={() => patchOps(o => { o[bk].requests = o[bk].requests || []; o[bk].requests.push({ id: 'r' + Date.now(), label: '', kind: 'document', note: '', status: 'pending' }); syncCount(o); })}>+ Add request</button>
        </div>
        <p className="hint" style={{ marginBottom: 8 }}>Use for onboarding documents and, for active clients, ID renewals / updated documents / additional info. The client sees each item, its type and status.</p>
        {reqs.length === 0 ? (
          <div className="empty"><b>No open requests</b><p>Add a document or action {firstName} needs to complete.</p></div>
        ) : (
          <div className="cal-list">
            {reqs.map((r: AnyObj, i: number) => (
              <div key={r.id || i} className="cal-row" style={{ gridTemplateColumns: '1fr 140px 110px 34px' }}>
                <input className={inp} placeholder="e.g. Passport copy / Trade licence" value={r.label} onChange={e => patchOps(o => { o[bk].requests[i].label = e.target.value; })} />
                <select className={inp} value={r.kind} onChange={e => patchOps(o => { o[bk].requests[i].kind = e.target.value; })}>
                  {KINDS.map(k => <option key={k.v} value={k.v}>{k.l}</option>)}
                </select>
                <select className={inp} value={r.status} onChange={e => patchOps(o => { o[bk].requests[i].status = e.target.value; syncCount(o); })}>
                  <option value="pending">Pending</option>
                  <option value="received">Received</option>
                </select>
                <button className="icon-btn" onClick={() => patchOps(o => { o[bk].requests.splice(i, 1); syncCount(o); })}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
      </Card>
    );
      }

      function renderDeliver() {
    if (!cur) return null;
    const delivs: AnyObj[] = cur.adminOps[isCorp ? 'acc' : 'con'].deliverables;
    return (
      <Card
        title="Deliver a document to the Data Room"
        sub="Uploads straight into the client's Data Room folder"
        foot={<>
          <span className="hint">{deliver.url ? 'Ready to publish: the client is notified in-app.' : 'Add a document name and choose a file to publish.'}</span>
          <button disabled={uploading || !deliver.url} className="btn btn-pri push-right" onClick={publishDoc}>
            <UploadCloud /> Publish to client portal
          </button>
        </>}
      >
        <div className="frow frow--2" style={{ marginBottom: 14 }}>
          <div><label className={lbl}>Document name</label><input className={inp} placeholder={isCorp ? 'e.g. VAT Certificate' : 'e.g. Capital Readiness Report'} value={deliver.name} onChange={e => setDeliver({ ...deliver, name: e.target.value })} /></div>
          <div><label className={lbl}>Data Room folder</label>
            <select className={inp} value={deliver.cat} onChange={e => setDeliver({ ...deliver, cat: e.target.value })}>
              {DELIVER_CATS.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </div>
        </div>
        <label className={`file-drop ${uploading ? 'is-uploading' : deliver.url ? 'has' : ''}`}>
          <UploadCloud className="w-4 h-4" />
          <span>
            {uploading ? `Uploading… ${uploadPct ?? 0}%` : deliver.url ? (deliver.name || 'File uploaded ✓') : 'Choose a file: it uploads straight into the client Data Room'}
          </span>
          <input type="file" disabled={uploading} onChange={e => { const f = e.target.files?.[0]; if (f) uploadDeliverable(f); e.currentTarget.value = ''; }} />
        </label>
        {deliver.url && !uploading && <a href={deliver.url} target="_blank" rel="noreferrer" className="drop-link">Preview uploaded file →</a>}
        {delivs.length > 0 && (
          <div className="deliv-list">
            {delivs.map((d: AnyObj, i: number) => (
              <div key={i} className="deliv-row">
                <span className="deliv-badge">DOC</span>
                <div className="deliv-meta"><b>{d.name}</b><span>{fmt(d.date)}{d.cat ? ` · ${d.cat}` : ''}</span></div>
                {d.url && <a href={d.url} target="_blank" rel="noreferrer" className="deliv-open">Open</a>}
                <span className={`deliv-pill ${d.downloaded ? 'done' : 'delivered'}`}>{d.downloaded ? 'Downloaded' : 'Delivered'}</span>
                <button className="icon-btn" onClick={() => patchOps(o => { o[isCorp ? 'acc' : 'con'].deliverables.splice(i, 1); })}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
      </Card>
    );
      }

      const planOptions = Array.from(new Set(list.map(c => c.plan).filter(Boolean))).sort();
      const activeFilters = [fStatus, fState, fExtra].filter(Boolean).length;
      const clearFilters = () => { setFStatus(''); setFState(''); setFExtra(''); };

      const matchesState = (c: AnyObj) => {
        if (fState === 'active') return c.activated;
        if (fState === 'onboarding') return !c.onboardingCompleted;
        if (fState === 'unverified') return !c.emailVerified;
        return true;
      };
      const matchesExtra = (c: AnyObj) => {
        if (!fExtra) return true;
        if (isCorp) return c.plan === fExtra;
        return fExtra === 'assessed' ? c.assessed : !c.assessed;
      };
      const filtered = list
        .filter(c => !q || (c.name + c.company + c.email).toLowerCase().includes(q.toLowerCase()) || clientIdMatches(c.uid, q))
        .filter(c => !fStatus || (fStatus === 'none' ? !c.orderStatus : c.orderStatus === fStatus))
        .filter(matchesState)
        .filter(matchesExtra)
        .sort((a, b) => {
          if (sort === 'name') return a.name.localeCompare(b.name);
          // Undated records sort last under both date orders rather than jumping to the top.
          const key = sort === 'seen' ? 'lastLoginAt' : 'createdAt';
          const av = a[key] ? +new Date(a[key]) : 0;
          const bv = b[key] ? +new Date(b[key]) : 0;
          return bv - av;
        });
      const chans = [notif.inApp && 'In-app', notif.wa && 'WhatsApp'].filter(Boolean) as string[];
      const displayName = cur ? (`${cur.firstName || ''} ${cur.lastName || ''}`.trim() || cur.name || cur.email || 'Client') : '';
      const firstName = displayName.split(' ')[0] || 'the client';

      // Live-preview derived data (all from real per-client records).
      const st = cur ? statusFor(cur) : null;
      const tone = st ? TONES[st.tone] : null;
      const deadlines: AnyObj[] = cur ? (cur.adminOps.acc.deadlines || []).slice().sort((a: AnyObj, b: AnyObj) => (a.date || '').localeCompare(b.date || '')) : [];
      const nextDeadline = deadlines.find(d => (d.date || '') >= TODAY()) || deadlines[0] || null;
      const pvDocs: AnyObj[] = cur ? cur.adminOps[isCorp ? 'acc' : 'con'].deliverables : [];

      return (
    <AdminLayout
      section="Overview"
      title="Command Center"
      icon={LayoutGrid}
      subtitle="Manage each client's status, deliverables and notifications. Everything you change here is reflected on their dashboard."
    >
      <style>{STYLES}</style>

      <div className="cc-grid">
        {/* CLIENT LIST */}
        <div className="card cc-clients">
          <div className="card-h">
            <b>Clients</b>
            <span>{loadingList && !list.length
              ? 'Loading…'
              : filtered.length === list.length
                ? `${list.length} client${list.length === 1 ? '' : 's'}`
                : `${filtered.length} of ${list.length}`}</span>
          </div>
          <div className="cc-search">
            <div className="cc-search__row">
              <div className="cc-search__box">
                <Search />
                <input placeholder="Search name, company, email or ID…" value={q} onChange={e => setQ(e.target.value)} />
              </div>
              <button
                type="button"
                className={`cc-filt-btn ${showFilters || activeFilters ? 'on' : ''}`}
                onClick={() => setShowFilters(v => !v)}
                title="Filters"
                aria-expanded={showFilters}
              >
                <SlidersHorizontal />
                {activeFilters > 0 && <i>{activeFilters}</i>}
              </button>
            </div>

            {showFilters && (
              <div className="cc-filters">
                <label>Order status</label>
                <div className="cc-chips">
                  {STATUS_FILTERS.map(([v, l]) => (
                    <button key={v || 'any'} type="button" className={fStatus === v ? 'on' : ''} onClick={() => setFStatus(v)}>{l}</button>
                  ))}
                </div>

                <label>Account</label>
                <select value={fState} onChange={e => setFState(e.target.value)}>
                  <option value="">Any state</option>
                  <option value="active">{isCorp ? 'Accounting active' : 'Consulting active'}</option>
                  <option value="onboarding">Onboarding incomplete</option>
                  <option value="unverified">Email unverified</option>
                </select>

                {isCorp ? (
                  <>
                    <label>Plan</label>
                    <select value={fExtra} onChange={e => setFExtra(e.target.value)} disabled={!planOptions.length}>
                      <option value="">{planOptions.length ? 'Any plan' : 'No plans recorded'}</option>
                      {planOptions.map(pn => <option key={pn} value={pn}>{pn}</option>)}
                    </select>
                  </>
                ) : (
                  <>
                    <label>Assessment</label>
                    <select value={fExtra} onChange={e => setFExtra(e.target.value)}>
                      <option value="">Any</option>
                      <option value="assessed">Completed</option>
                      <option value="unassessed">Not completed</option>
                    </select>
                  </>
                )}

                <label>Sort by</label>
                <select value={sort} onChange={e => setSort(e.target.value as 'new' | 'name' | 'seen')}>
                  <option value="new">Newest first</option>
                  <option value="name">Name A–Z</option>
                  <option value="seen">Recently active</option>
                </select>

                {activeFilters > 0 && (
                  <button type="button" className="cc-filt-clear" onClick={clearFilters}>
                    Clear {activeFilters} filter{activeFilters === 1 ? '' : 's'}
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="cc-list">
            {filtered.map(c => (
              <button key={c.uid} onClick={() => selectClient(c.uid)} className={`cc-client ${cur?.uid === c.uid ? 'active' : ''}`}>
                <span className="avatar">{initials(c.name)}</span>
                <div style={{ minWidth: 0 }}>
                  <b>{c.name}</b>
                  <span>{c.company || c.email}</span>
                  <span className="cc-id">{clientId(c.uid)}</span>
                </div>
              </button>
            ))}
            {loadingList && !list.length && (
              <div className="empty"><b>Loading clients…</b><p>Fetching your client list, one moment.</p></div>
            )}
            {!loadingList && !filtered.length && (
              <div className="empty">
                <b>No clients found</b>
                <p>{!list.length
                  ? 'No clients yet.'
                  : activeFilters
                    ? 'No client matches these filters.'
                    : 'Try a different name, company or email.'}</p>
                {activeFilters > 0 && <button type="button" className="cc-filt-clear" onClick={clearFilters}>Clear filters</button>}
              </div>
            )}
          </div>
        </div>

        {/* DETAIL STACK */}
        <div className="cc-stack">
          {!cur ? (
            <div className="card"><div className="card-b cc-placeholder">Select a client to begin…</div></div>
          ) : (<>
            {/* Full account & profile — at the TOP */}
            <ClientAccountView embedded uid={cur.uid} onDeleted={() => { setCur(null); fetchList(); }} />

            {/* Manage & send — the editable / sendable operations, BELOW the profile */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '14px 2px 2px' }}>
              <span style={{ flex: 1, height: 1, background: 'var(--line)' }} />
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.8px', textTransform: 'uppercase', color: 'var(--faint)' }}>Manage &amp; send</span>
              <span style={{ flex: 1, height: 1, background: 'var(--line)' }} />
            </div>

            {isCorp ? (<>
              <Card
                title="Compliance status"
                sub="What the client owes next, shown on their dashboard"
                foot={<>
                  <span className="hint">Changes save to the client dashboard automatically.</span>
                  <span className="saved" style={{ opacity: flash ? 1 : 0 }}>✓ Saved to dashboard</span>
                </>}
              >
                <div className="frow frow--2">
                  <div><label className={lbl}>Next filing name</label><input className={inp} placeholder="e.g. VAT return Q3 2026" value={cur.adminOps.acc.nextName} onChange={e => patchOps(o => { o.acc.nextName = e.target.value; })} /></div>
                  <div><label className={lbl}>Next filing date</label><input type="date" className={inp} value={cur.adminOps.acc.nextDate} onChange={e => patchOps(o => { o.acc.nextDate = e.target.value; })} /></div>
                </div>
                <div className="frow frow--2 row-gap">
                  <div><label className={lbl}>Overdue?</label><select className={inp} value={cur.adminOps.acc.overdue ? 'yes' : 'no'} onChange={e => patchOps(o => { o.acc.overdue = e.target.value === 'yes'; })}><option value="no">No</option><option value="yes">Yes · past due</option></select></div>
                  <div><label className={lbl}>Setup complete?</label><select className={inp} value={cur.adminOps.acc.setupComplete ? 'yes' : 'no'} onChange={e => patchOps(o => { o.acc.setupComplete = e.target.value === 'yes'; })}><option value="yes">Yes</option><option value="no">No</option></select></div>
                </div>
              </Card>

              <Card
                title="Compliance calendar"
                sub="Deadlines saved to this client's dashboard"
                headExtra={<span>{cur.adminOps.acc.deadlines.length} item{cur.adminOps.acc.deadlines.length === 1 ? '' : 's'}</span>}
              >
                <div className="cal-tools">
                  <button className="btn btn-pri" onClick={generateCalendar}><CalendarPlus /> Generate from client data</button>
                  <button className="btn" onClick={() => patchOps(o => { o.acc.deadlines.push({ name: 'New item', date: TODAY(), type: 'VAT' }); })}>+ Add deadline</button>
                </div>
                <p className="hint" style={{ marginBottom: 8 }}>Generates trade-licence renewal, VAT, Corporate Tax &amp; ESR dates from this client's licence and incorporation dates. Review, edit, then it saves to their dashboard automatically.</p>
                {cur.adminOps.acc.deadlines.length === 0 ? (
                  <div className="empty"><b>No deadlines yet</b><p>Generate them from {firstName}'s licence data, or add one manually.</p></div>
                ) : (
                  <div className="cal-list">
                    {cur.adminOps.acc.deadlines.map((d: AnyObj, i: number) => (
                      <div key={i} className="cal-row">
                        <input className={`${inp} cal-name`} value={d.name} onChange={e => patchOps(o => { o.acc.deadlines[i].name = e.target.value; })} />
                        <input type="date" className={`${inp} cal-date`} value={d.date} onChange={e => patchOps(o => { o.acc.deadlines[i].date = e.target.value; })} />
                        <input className={`${inp} cal-type`} value={d.type} placeholder="Type" onChange={e => patchOps(o => { o.acc.deadlines[i].type = e.target.value; })} />
                        <button className="icon-btn" onClick={() => patchOps(o => { o.acc.deadlines.splice(i, 1); })}><Trash2 className="w-4 h-4" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              {renderRequests()}
              {renderDeliver()}
            </>) : (<>
              <Card
                title="Readiness status"
                sub="Shown on the client's Capital Readiness dashboard"
                foot={<>
                  <span className="hint">Changes save to the client dashboard automatically.</span>
                  <span className="saved" style={{ opacity: flash ? 1 : 0 }}>✓ Saved to dashboard</span>
                </>}
              >
                <div className="frow frow--2">
                  <div><label className={lbl}>Capital readiness score (0–100)</label><input type="number" min={0} max={100} className={inp} value={cur.adminOps.con.readiness ?? ''} onChange={e => patchOps(o => { o.con.readiness = e.target.value === '' ? null : Math.max(0, Math.min(100, +e.target.value || 0)); })} /></div>
                  <div><label className={lbl}>Profile completion (%)</label><input type="number" min={0} max={100} className={inp} value={cur.adminOps.con.profile} onChange={e => patchOps(o => { o.con.profile = Math.max(0, Math.min(100, +e.target.value || 0)); })} /></div>
                </div>
              </Card>
              {renderRequests()}
              {renderDeliver()}
            </>)}

            {/* Notification composer */}
            <Card
              title="Push a notification"
              sub="Sends to the client's portal and chosen channels"
              foot={<>
                <span className="hint">{chans.length ? `Sends via ${chans.join(' + ')}.` : 'Select at least one channel.'}</span>
                <button className="btn btn-pri push-right" disabled={!notif.title || !chans.length} onClick={pushComposed}><Send /> Send notification</button>
              </>}
            >
              <div className="frow frow--2" style={{ marginBottom: 14 }}>
                <div><label className={lbl}>Quick template</label>
                  <select className={inp} value={notif.tpl} onChange={e => { const t = e.target.value; setNotif(n => ({ ...n, tpl: t, ...(TPL[t] ? { title: TPL[t][0], msg: TPL[t][1] } : {}) })); }}>
                    <option value="">Choose a template</option>
                    <option value="due">Filing due reminder</option>
                    <option value="filed">Filed, you're compliant</option>
                    <option value="docs">Documents requested</option>
                    <option value="report">Report ready to download</option>
                  </select>
                </div>
                <div><label className={lbl}>Title</label><input className={inp} value={notif.title} onChange={e => setNotif({ ...notif, title: e.target.value })} placeholder="e.g. Your report is ready" /></div>
              </div>
              <div style={{ marginBottom: 14 }}><label className={lbl}>Message</label><textarea rows={2} className={inp} value={notif.msg} onChange={e => setNotif({ ...notif, msg: e.target.value })} placeholder="What should the client know?" /></div>
              <div>
                <label className={lbl}>Channels</label>
                <div className="chan">
                  {([['inApp', 'In-app'], ['wa', 'WhatsApp']] as const).map(([k, label]) => (
                    <button key={k} type="button" onClick={() => setNotif(n => ({ ...n, [k]: !n[k] }))} className={(notif as AnyObj)[k] ? 'active' : ''}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </Card>
          </>)}
        </div>

        {/* LIVE PREVIEW */}
        <div className="preview">
          <div className="phead"><b>{isCorp ? 'Accounting & Tax' : 'Capital Readiness'}</b><span>Live preview</span></div>
          <div className="pbody">
            {!cur || !st || !tone ? (
              <p className="pempty">Select a client to preview their dashboard.</p>
            ) : (<>
              <p className="hint" style={{ marginBottom: 10 }}>What <b>{firstName}</b> sees now</p>
              <div className="pcard">
                <b><span className="dot" style={{ background: tone.dot }} />{st.label}</b>
                <p>{st.d}</p>
                {(st as AnyObj).cta && <a href="#" onClick={e => e.preventDefault()}>{(st as AnyObj).cta} →</a>}
              </div>

              {isCorp && (<>
                <div className="plabel">Next deadline</div>
                {nextDeadline ? (
                  <div className="pnote"><b>{nextDeadline.name}</b><p>{fmt(nextDeadline.date)}{nextDeadline.type ? ` · ${nextDeadline.type}` : ''}</p></div>
                ) : <p className="pempty">No deadlines on the calendar yet</p>}
              </>)}

              <div className="plabel"><Bell className="w-3.5 h-3.5" /> Notification center</div>
              {sent.length ? sent.slice(0, 3).map((n, i) => (
                <div key={i} className="pnote">
                  <b>{n.title}</b>
                  <p>{n.msg}</p>
                  <time>{n.time} · {n.ch.join(' + ')}</time>
                </div>
              )) : <p className="pempty">No notifications sent yet</p>}

              <div className="plabel">Data Room</div>
              {pvDocs.length ? pvDocs.map((d: AnyObj, i: number) => (
                <div key={i} className="pnote"><b>{d.name}</b><p>{d.cat || 'Data Room'}{d.date ? ` · ${fmt(d.date)}` : ''}</p></div>
              )) : <p className="pempty">Data Room is empty</p>}
            </>)}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
